import { IFaireMoney } from '@faire/web-api--source/indigofair/data/IFaireMoney';
import { IMembershipBenefit } from '@faire/web-api--source/indigofair/data/IMembershipBenefit';
import { IPayment } from '@faire/web-api--source/indigofair/data/IPayment';

export namespace IMembership {
  export enum CancelReason {
    /**
     * Brands I shop aren't covered
     */
    BRAND_COVERAGE = 'BRAND_COVERAGE',
    /**
     * I haven't saved enough to justify the membership
     */
    NOT_ENOUGH_SAVING = 'NOT_ENOUGH_SAVING',
    /**
     * Program benefits aren't that useful to me
     */
    NOT_ENOUGH_BENEFIT = 'NOT_ENOUGH_BENEFIT',
    /**
     * I'm not ordering enough on Faire
     */
    LOW_ORDER_VOLUME = 'LOW_ORDER_VOLUME',
    /**
     * I don't have the budget available
     */
    NO_BUDGET = 'NO_BUDGET',
    /**
     * Other (please specify):
     */
    OTHER = 'OTHER',
    /**
     * Retailer is blacklisted on renewal
     */
    BLACKLISTED = 'BLACKLISTED',
    /**
     * Retailer is bankrupted on renewal
     */
    BANKRUPTED = 'BANKRUPTED',
    /**
     * Retailer identity is not confirmed on renewal
     */
    IDENTITY_NOT_CONFIRMED = 'IDENTITY_NOT_CONFIRMED',
    /**
     * Retailer claimed Shopify offer on renewal
     */
    SHOPIFY_OFFER_CLAIMED = 'SHOPIFY_OFFER_CLAIMED',
    /**
     * Membership canceled by admin upon request
     */
    CANCELED_BY_ADMIN = 'CANCELED_BY_ADMIN',
    /**
     * Auto Enrolled free trial ended
     */
    AUTO_ENROLLED_TRIAL_ENDED = 'AUTO_ENROLLED_TRIAL_ENDED',
  }

  export enum Type {
    /**
     * Faire Direct
     * @deprecated
     */
    ELEVATE = 'ELEVATE',
    /**
     * Insider
     */
    SELECT = 'SELECT',
    /**
     * ONLY used for Brand Order creation when determining brand_order.retailer_membership_type
     * Use this type when the membership state is either TRIALING or AUTO_ENROLLED_TRIAL
     */
    SELECT_TRIALING = 'SELECT_TRIALING',
  }

  export enum State {
    /**
     * We aren't charging the retailer/brand, until the current period ends.
     */
    TRIALING = 'TRIALING',
    ACTIVE = 'ACTIVE',
    /**
     * There is a failed payment for this membership.
     */
    UNPAID = 'UNPAID',
    CANCELED = 'CANCELED',
    PAUSED = 'PAUSED',
    AUTO_ENROLLED_TRIAL = 'AUTO_ENROLLED_TRIAL',
  }

  export enum ActionAtPeriodEnd {
    ACTION_AT_PERIOD_END_UNKNOWN = 'ACTION_AT_PERIOD_END_UNKNOWN',
    RENEW = 'RENEW',
    CANCEL = 'CANCEL',
    PAUSE = 'PAUSE',
  }

  /**
   * How often we should charge for the subscription.
   */
  export enum ChargeInterval {
    /**
     * A month in this situation is 30 days.
     */
    MONTH = 'MONTH',
    /**
     * A year in this situation is 365 days.
     */
    YEAR = 'YEAR',
  }

  export enum Channel {
    CHANNEL_UNKNOWN = 'CHANNEL_UNKNOWN',
    SHOPIFY = 'SHOPIFY',
    AUTOMATIC_FREE_TRIAL = 'AUTOMATIC_FREE_TRIAL',
    AUTOMATIC_FREE_TRIAL_RENEWAL = 'AUTOMATIC_FREE_TRIAL_RENEWAL',
    MARKETING_PAGE = 'MARKETING_PAGE',
    SHOPPING_BAG = 'SHOPPING_BAG',
    BRAND_PAGE = 'BRAND_PAGE',
    PDP = 'PDP',
    CHECKOUT = 'CHECKOUT',
    CX_ADMIN = 'CX_ADMIN',
    HOMEPAGE = 'HOMEPAGE',
    MOBILE_BANNER = 'MOBILE_BANNER',
    CAMPAIGN_LANDING_PAGE = 'CAMPAIGN_LANDING_PAGE',
  }

  /**
   * Return an instance of IMembership with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<IMembership>): IMembership =>
    ({
      ...(partial ?? {}),
    }) as IMembership;
}

export interface IMembership {
  token: string | undefined;
  type: keyof typeof IMembership.Type | undefined;
  state: keyof typeof IMembership.State | undefined;
  action_at_period_end: keyof typeof IMembership.ActionAtPeriodEnd | undefined;
  bucket_migration_on_cancellation: string | undefined;
  current_period_start_at: number | undefined;
  current_period_end_at: number | undefined;
  /**
   * @deprecated
   */
  cost_cents: number | undefined;
  /**
   * @deprecated
   */
  incoming_cost_cents: number | undefined;
  /**
   * monthly cost
   */
  cost: IFaireMoney | undefined;
  incoming_cost: IFaireMoney | undefined;
  cancel_at_period_end: boolean | undefined;
  pause_at_period_end: boolean | undefined;
  canceled_at: number | undefined;
  paused_at: number | undefined;
  paused_until: number | undefined;
  trialing_period_days: number | undefined;
  brand_token: string | undefined;
  retailer_token: string | undefined;
  active: boolean | undefined;
  charge_interval: keyof typeof IMembership.ChargeInterval | undefined;
  current_period_payment: IPayment | undefined;
  send_billing_reminders: boolean | undefined;
  cancel_note: string | undefined;
  membership_version: number | undefined;
  cancel_reason: keyof typeof IMembership.CancelReason | undefined;
  membership_benefit: IMembershipBenefit | undefined;
  version: number | undefined;
  created_at: number | undefined;
  /**
   * @deprecated
   */
  updated_at: number | undefined;
  terms_accepted_at: number | undefined;
  channel: keyof typeof IMembership.Channel | undefined;
  is_on_retention_offer: boolean | undefined;
  /**
   * monthly cost of annual membership
   */
  annually_billed_cost: IFaireMoney | undefined;
}
